// Type declarations for global window properties
declare global {
  interface Window {
    Shopify: {
      routes: {
        root: string;
      };
    };
  }
}

type ModalAction = 'open' | 'close' | 'toggle';

// Base modal class
class GModal extends HTMLElement {
  protected _content: HTMLElement | null = null;
  protected _contentWrapper: HTMLElement | null = null;
  protected backdrop: HTMLElement | null = null;
  protected enableDrag: string | null;

  constructor() {
    super();
    this.enableDrag = this.getAttribute('drag');
  }

  connectedCallback(): void {
    this.backdrop = this.querySelector(".js-backdrop");
    if (this.backdrop) {
      this.backdrop.addEventListener("click", this.close.bind(this));
    }

    if (this.enableDrag === 'true') {
      this.initDragClose();
    }
  }

  open(): void {
    document.documentElement.style.overflow = "hidden";
    this.expanded = true;
  }

  close(): void {
    document.documentElement.style.overflow = "";
    this.expanded = false;
  }

  toggle(): void {
    this.expanded ? this.close() : this.open();
  }

  onTrigger(event: string, triggerEl: HTMLElement | null, action: ModalAction = "open", _contentKey?: string): void {
    if (!triggerEl) {
      console.error("Trigger element isn't specified");
      return;
    }
    const actionMethod = this[action as keyof this];
    if (typeof actionMethod === 'function') {
      triggerEl.addEventListener(event, (actionMethod as Function).bind(this));
    }
  }

  initDragClose(): void {
    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    let isDragEnabled = false;
    const threshold = window.innerHeight / 3;
    const defaultOpacity = 0.3;

    this.contentWrapper?.addEventListener("touchstart", (e: TouchEvent) => {
      if (!this.contentWrapper) return;
      
      const contentRect = this.contentWrapper.getBoundingClientRect();
      const isTopVisible = contentRect.top >= 0;

      if (!isTopVisible) {
        return;
      }

      const touchY = e.touches[0].clientY;
      if (window.innerWidth >= 768) {
        isDragEnabled = false;
        return;
      }

      isDragEnabled = true;
      this.contentWrapper.style.transition = "none";
      startY = touchY;
      isDragging = true;
    });

    this.contentWrapper?.addEventListener("touchmove", (e: TouchEvent) => {
      if (!isDragEnabled || !isDragging || !this.backdrop || !this.contentWrapper || !this.content) return;

      e.preventDefault();
      currentY = e.touches[0].clientY;
      const diffY = currentY - startY;

      if (diffY > 0) {
        this.contentWrapper.style.transform = `translate3d(0, ${diffY.toFixed(2)}px, 0)`;
        this.content.style.overflow = "hidden";
        
        // Update backdrop opacity based on proximity to threshold
        const thresholdRatio = Math.min(diffY / threshold, 1);
        const newOpacity = defaultOpacity * (1 - thresholdRatio);
        this.backdrop.style.setProperty('--tw-bg-opacity', newOpacity.toFixed(3));
      } else {
        this.contentWrapper.style.removeProperty("transform");
        // Reset backdrop opacity when not dragging down
        this.backdrop.style.setProperty('--tw-bg-opacity', String(defaultOpacity));
      }
    });

    this.contentWrapper?.addEventListener("touchend", () => {
      if (!isDragEnabled || !this.backdrop || !this.contentWrapper || !this.content) return;

      this.contentWrapper.style.removeProperty("transition");
      this.content.style.overflow = "";
      const diffY = currentY - startY;

      if (diffY > threshold) {
        // close the modal and reset the transform
        this.close();
        requestAnimationFrame(() => {
          if (!this.contentWrapper || !this.backdrop) return;
          
          this.contentWrapper.style.removeProperty("transform");
          this.contentWrapper.style.removeProperty("transition");
          const transitionDuration = getComputedStyle(this.backdrop).getPropertyValue('--modal-transition-duration');
          const duration = transitionDuration ? parseInt(transitionDuration) : 300;

          setTimeout(() => {
            this.backdrop?.style.setProperty("--tw-bg-opacity", String(defaultOpacity));
          }, duration);
        });
      } else {
        requestAnimationFrame(() => {
          this.contentWrapper?.style.removeProperty("transform");
        });

        this.backdrop.style.setProperty("--tw-bg-opacity", String(defaultOpacity));
      }

      isDragging = false;
      isDragEnabled = false;
    });
  }

  get expanded(): boolean {
    return this.getAttribute("aria-expanded") === "true";
  }

  set expanded(value: boolean) {
    this.setAttribute("aria-expanded", String(Boolean(value)));
  }

  get contentWrapper(): HTMLElement | null {
    if (!this._contentWrapper) {
      this._contentWrapper = this.querySelector(".js-content-wrapper");
    }
    return this._contentWrapper;
  }

  get content(): HTMLElement | null {
    if (!this._content) {
      this._content = this.querySelector(".js-content");
    }
    return this._content;
  }
}

// Dynamic modal class
class DynamicModal extends GModal {
  protected currentContentKey: string | null = null;

  constructor() {
    super();
  }

  onTrigger(event: string, triggerEl: HTMLElement | null, action: ModalAction = "open", contentKey?: string): void {
    if (!triggerEl) {
      console.error("Trigger element isn't specified");
      return;
    }

    if (["open", "toggle"].includes(action) && !contentKey) {
      console.error("Content key is required for action: ", action);
      return;
    }

    triggerEl.addEventListener(event, () => {
      const actionMethod = this[action as keyof this];
      if (typeof actionMethod === 'function') {
        (actionMethod as Function).call(this, contentKey);
      }
    });
  }

  open(contentKey?: string): void {
    if (this.dataset.checkContent === "true" && this.currentContentKey !== contentKey && contentKey) {
      const content = document.querySelector(`[data-modal-content="${contentKey}"]`);

      if (!content) {
        console.error("Content wasn't found");
        return;
      }

      this.currentContentKey = contentKey;
      if (this.content) {
        this.content.innerHTML = content.innerHTML;
      }
    }

    super.open();
  }

  toggle(contentKey?: string): void {
    this.expanded ? this.close() : this.open(contentKey);
  }
}

// Fetch modal class
class FetchModal extends DynamicModal {
  async open(contentKey?: string, ...args: unknown[]): Promise<void | boolean> {
    if (this.currentContentKey !== contentKey && contentKey) {
      try {
        const response = await fetch(`?section_id=${contentKey}`);
        if (!response.ok) {
          console.error("Failed to fetch content");
          return;
        }

        const text = await response.text();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = text;

        const content = tempDiv.querySelector("#fetch-content");
        if (!content) {
          console.error("Content wasn't found");
          return;
        }

        this.currentContentKey = contentKey;
        if (this.content) {
          this.content.innerHTML = content.innerHTML;
        }
      } catch (error) {
        console.error("Error fetching content:", error);
        return;
      }
    }

    document.documentElement.style.overflow = "hidden";
    this.expanded = true;
  }
}

// Product pop-up modal
export class FetchModalProductPopUp extends FetchModal {
  previousVariantId: string | null = null;
  currentCategory: string | null = null;
  _lastTriggerEl: HTMLElement | null = null;

  constructor() {
    super();
  }

  override onTrigger(event: string, triggerEl: HTMLElement | null, action: ModalAction = "open", contentKey?: string): void {
    if (!triggerEl) {
      console.error("Trigger element isn't specified");
      return;
    }

    if (["open", "toggle"].includes(action) && !contentKey) {
      console.error("Content key is required for action: ", action);
      return;
    }

    triggerEl.addEventListener(event, () => {
      // Read contentKey and variantId at click time (not setup time) so dynamic updates work
      const currentContentKey = triggerEl.dataset.contentKey || contentKey;
      const variantId = triggerEl.dataset.variantId || null;
      const category = triggerEl.dataset.category || null;
      
      // Only update _lastTriggerEl for open action, not for close
      // This preserves the original open trigger for re-fetch checks
      if (action === 'open') {
        this._lastTriggerEl = triggerEl;
      }
      
      const actionMethod = this[action as keyof this];
      if (typeof actionMethod === 'function') {
        (actionMethod as Function).call(this, currentContentKey, variantId, category);
      }
    });
  }

  override async open(productHandle?: string, variantId?: string | null, category: string | null = null): Promise<boolean> {
    // Store category for use after content loads
    this.currentCategory = category;
    const isNewContent = this.previousVariantId !== variantId;
    
    if (isNewContent) {
      this.contentWrapper?.setAttribute("data-skeleton", "true");
    }

    this.setAttribute("aria-expanded", "true");
    document.documentElement.style.overflow = "hidden";
    
    if (!isNewContent) {
      this.contentWrapper?.setAttribute("data-skeleton", "false");
      return false; // Return false to indicate no new content was fetched
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}?variant=${variantId}&section_id=g-ajax-mix-and-match-modal`);
      if (!response.ok) throw new Error("Failed to fetch content");

      const htmlContent = await response.text();
      const domParser = new DOMParser();
      const newContent = domParser.parseFromString(htmlContent, "text/html").querySelector("[data-content]");

      if (newContent && this.content) {
        this.content.innerHTML = newContent.innerHTML;
      }
    } catch (error) {
      console.error("Error loading popup:", error);
    }

    this.contentWrapper?.setAttribute("data-skeleton", "false");
    this.previousVariantId = variantId || null;
    return true; // Return true to indicate new content was fetched
  }
}

// Modal trigger class
class ModalTrigger extends HTMLElement {
  connectedCallback(): void {
    const modalSelector = this.dataset.modalSelector || "g-modal";
    const event = this.dataset.event || "click";
    const action = (this.dataset.action || "open") as ModalAction;
    const contentKey = this.dataset.contentKey;

    const modal = document.querySelector(modalSelector) as GModal | null;
    if (!modal) {
      console.error("Modal wasn't found");
      return;
    }

    // If modal has onTrigger, call it directly
    if (typeof modal.onTrigger === 'function') {
      modal.onTrigger(event, this, action, contentKey);
    } else {
      // Modal custom element not upgraded yet, wait for it
      const tagName = modal.tagName.toLowerCase();
      customElements.whenDefined(tagName).then(() => {
        if (typeof modal.onTrigger === 'function') {
          modal.onTrigger(event, this, action, contentKey);
        } else {
          console.error("Modal doesn't have onTrigger method");
        }
      });
    }
  }
}

// Define custom elements
customElements.define("g-modal", GModal);
customElements.define("dynamic-modal", DynamicModal);
customElements.define("fetch-modal", FetchModal);
customElements.define("fetch-modal-product-pop-up", FetchModalProductPopUp);
customElements.define("modal-trigger", ModalTrigger);

